// ReplyFooter — docked below every coach reply.
//
// Order (per designer handoff Q2): sources → recall → follow-ups → feedback.
// Keeps the user invited to continue (follow-up) rather than dismissed, and
// gives memory + sources the quiet role that earns trust without stealing
// attention from the reply itself.
//
// Recall and follow-ups are hidden when empty — V1 ships without those two
// backend channels (memory lands in MR D, follow-up generation in a later
// engine upgrade), so the footer gracefully degrades to just sources +
// feedback until they do.

import { useEffect, useState } from 'react';
import type { CoachCitation } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { ConsultedPanel } from './ConsultedPanel.js';
import {
  FeedbackInline,
  type FeedbackReason,
  type FeedbackVerdict,
} from './FeedbackInline.js';
import { SourcesLine } from './SourcesLine.js';
import styles from './Coach.module.css';

export interface RecallNoteRef {
  /** `inferred` | `user_stated` — maps 1:1 to the memory_notes doc field. */
  source: 'user_stated' | 'inferred';
  text: string;
  /** Optional id to deep-link into the Notebook page. */
  noteId?: string;
}

export interface ReplyFooterProps {
  citations: readonly CoachCitation[];
  /** Notes drawn on for this reply. V1: empty. */
  recallNotes?: readonly RecallNoteRef[];
  /** Suggested follow-up prompts. V1: empty. */
  followUps?: readonly string[];
  onFollowUpClick?: (text: string) => void;
  onOpenNotebook?: () => void;
  // Feedback — initial verdict + callbacks.
  verdict: FeedbackVerdict;
  onVerdict: (v: FeedbackVerdict) => void;
  onSubmitReason: (reason: FeedbackReason | null, note: string) => void | Promise<void>;
  /** The 1-indexed citation marker the user clicked in the reply body, if
   *  any. Opens the ConsultedPanel pre-expanded to that row. The parent
   *  resets this to null after use. */
  openCitationNumber?: number | null;
  onCitationHandled?: () => void;
}

export function ReplyFooter({
  citations,
  recallNotes = [],
  followUps = [],
  onFollowUpClick,
  onOpenNotebook,
  verdict,
  onVerdict,
  onSubmitReason,
  openCitationNumber = null,
  onCitationHandled,
}: ReplyFooterProps) {
  const { t, tt } = useI18n();
  const [recallOpen, setRecallOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [highlightNumber, setHighlightNumber] = useState<number | null>(null);

  // When the parent passes a citation number (from an inline marker click),
  // open the panel + highlight that row. Then notify parent so it can reset.
  useEffect(() => {
    if (openCitationNumber !== null) {
      setSourcesOpen(true);
      setHighlightNumber(openCitationNumber);
      onCitationHandled?.();
    }
  }, [openCitationNumber, onCitationHandled]);

  const hasRecall = recallNotes.length > 0;
  const hasFollowUps = followUps.length > 0;

  return (
    <div className={styles.rfoot}>
      <SourcesLine
        citations={citations}
        panelOpen={sourcesOpen}
        onToggle={() => {
          setSourcesOpen((o) => !o);
          setHighlightNumber(null);
        }}
      />
      {sourcesOpen && citations.length > 0 && (
        <ConsultedPanel citations={citations} highlightNumber={highlightNumber} />
      )}

      {hasRecall && (
        <>
          <button
            type="button"
            className={styles.recall}
            onClick={() => setRecallOpen((o) => !o)}
            aria-expanded={recallOpen}
          >
            ◈ {tt('coach.recall', { n: recallNotes.length })} {recallOpen ? '▾' : '▸'}
          </button>
          {recallOpen && (
            <div className={styles.rlist2}>
              {recallNotes.map((n, i) => (
                <div key={i}>
                  <span className={n.source === 'inferred' ? styles.tagInf : styles.tagUser}>
                    {n.source === 'inferred' ? t('memory.src.inferred.badge') : t('coach.you')}
                  </span>
                  {n.text}
                </div>
              ))}
              <div className={styles.rlist2Footer}>
                <button type="button" className={styles.rlist2OpenLink} onClick={onOpenNotebook}>
                  {t('coach.recall.open')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {hasFollowUps && (
        <div className={styles.fups}>
          {followUps.map((f, i) => (
            <button
              key={i}
              type="button"
              className={styles.fup}
              onClick={() => onFollowUpClick?.(f)}
            >
              → {f}
            </button>
          ))}
        </div>
      )}

      <FeedbackInline
        verdict={verdict}
        onVerdict={onVerdict}
        onSubmitReason={onSubmitReason}
      />
    </div>
  );
}
